import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getDepartments, deleteDepartment, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaBuilding, FaFileAlt, FaCalendarAlt, FaPrint, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { getPdfRowStyles } from '../../../../shared/utils.js/utils';
import { useLanguageChange } from '../../../../shared/hooks/useLanguageChange';
import { isOnline } from '../../../../shared/utils.js/offlineQueue';
import SortOrderSelect from '../../../../shared/components/SortOrderSelect';

const Departments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { departments, isLoading, error, success } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [rawDepartments, setRawDepartments] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  
  const [departmentForPrint, setDepartmentForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewSingleOpen, setIsPreviewSingleOpen] = useState(false);
  const [departmentForPreview, setDepartmentForPreview] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allDepartmentsForPdf, setAllDepartmentsForPdf] = useState([]);
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('name');

  useEffect(() => {
    setTitle('sidenav.hr.departments');
  }, [setTitle]);

  // Load all departments from Redux/API with high limit (per_page: 1000)
  const fetchAllDepartments = useCallback(async () => {
    setIsLoadingDepartments(true);
    try {
      const params = { language: i18n.language, page: 1, per_page: 1000 };
      const result = await dispatch(getDepartments(params)).unwrap();
      const responseData = result?.data || result;
      let departmentsList = [];
      if (Array.isArray(responseData)) {
        departmentsList = responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        departmentsList = responseData.data;
      }
      setRawDepartments(departmentsList);
    } catch (err) {
      if (err !== 'OFFLINE_REQUEST_SAVED') {
        console.error('Failed to fetch departments:', err);
      }
    } finally {
      setIsLoadingDepartments(false);
    }
  }, [dispatch, i18n.language]);

  // Fetch departments on mount and when language changes
  useEffect(() => {
    fetchAllDepartments();
  }, [fetchAllDepartments]);

  // Use language change hook to ensure data refetches
  useLanguageChange(() => {
    fetchAllDepartments();
  }, [dispatch]);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      if (isOnline()) {
        fetchAllDepartments();
      }
    };

    const handleOfflineRequestsRetried = (event) => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results?.some(r =>
        r.success === true &&
        (r.request?.url?.includes('/departments') || r.request?.url?.includes('/department'))
      )) {
        if (isOnline()) {
          fetchAllDepartments();
        }
      }
    };

    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    window.addEventListener('offlineRequestsRetried', handleOfflineRequestsRetried);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
      window.removeEventListener('offlineRequestsRetried', handleOfflineRequestsRetried);
    };
  }, [fetchAllDepartments]);

  // Handle success notifications and list reloading
  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      fetchAllDepartments();
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL, fetchAllDepartments]);

  // Client-side search and sort
  const filteredDepartments = React.useMemo(() => {
    // Fallback to Redux store departments if rawDepartments state is empty
    const departmentsSource = rawDepartments.length > 0 
      ? rawDepartments 
      : (Array.isArray(departments) ? departments : []);

    let data = [...departmentsSource];
    const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    
    if (query) {
      data = data.filter(d => 
        (d.name || '').toLowerCase().includes(query) ||
        (d.description || '').toLowerCase().includes(query)
      );
    }

    return data.sort((a, b) => {
      if (sortField === 'created_at') {
        const aVal = new Date(a.created_at || 0).getTime();
        const bVal = new Date(b.created_at || 0).getTime();
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aVal = (a.name || '').toLowerCase();
      const bVal = (b.name || '').toLowerCase();
      if (sortOrder === 'asc') return aVal.localeCompare(bVal, isRTL ? 'ar' : 'en');
      return bVal.localeCompare(aVal, isRTL ? 'ar' : 'en');
    });
  }, [rawDepartments, departments, searchTerm, sortField, sortOrder, isRTL]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredDepartments.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedDepartments = filteredDepartments.slice((currentPageSafe - 1) * perPage, currentPageSafe * perPage);

  const handleApplyPerPage = () => {
    const newPerPage = Math.max(1, Math.min(perPageInput, filteredDepartments.length || 1000));
    setPerPage(newPerPage);
    setPerPageInput(newPerPage);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    navigate('/departments/add');
  };

  const handleEdit = (id) => {
    navigate(`/departments/edit/${id}`);
  };

  const handleView = (department) => {
    setDepartmentForPreview(department);
    setIsPreviewSingleOpen(true);
  };

  const handleDelete = (department) => {
    setDepartmentToDelete(department);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (departmentToDelete) {
      const idToDelete = departmentToDelete.id;
      // Optimistically remove from state
      setRawDepartments(prev => prev.filter(department => department.id !== idToDelete));
      setShowDeleteModal(false);
      const backup = departmentToDelete;
      setDepartmentToDelete(null);

      try {
        const result = await dispatch(deleteDepartment(idToDelete));
        if (deleteDepartment.rejected.match(result)) {
          // Rollback on error
          setRawDepartments(prev => [...prev, backup].sort((a, b) => (a.id || 0) - (b.id || 0)));
        }
      } catch (error) {
        setRawDepartments(prev => [...prev, backup].sort((a, b) => (a.id || 0) - (b.id || 0)));
      }
    }
  };

  const colStyles = {
    serial: { width: '8%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    name: { width: '30%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    description: { width: '42%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    created: { width: '20%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' }
  };

  const calculateDynamicChunks = (items) => {
    const tableContainer = document.getElementById('departments-measurement-container');
    const thead = tableContainer ? tableContainer.querySelector('thead') : null;
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer ? tableContainer.querySelectorAll('tbody tr') : [];
    
    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      let rowHeight = 45; // Default fallback height
      
      if (rowElement && rowElement.offsetHeight > 0) {
        rowHeight = rowElement.offsetHeight;
      } else {
        const nameLen = (item.name || '').length;
        const descLen = (item.description || '').length;
        const maxTextLen = Math.max(nameLen / 30, descLen / 54);
        const lines = Math.max(1, Math.ceil(maxTextLen));
        rowHeight = 35 + (lines * 15);
      }

      if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // Handle preview all departments
  const handlePreviewAll = async () => {
    if (!filteredDepartments || filteredDepartments.length === 0) {
      toast.error(t('departments.preview.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setAllDepartmentsForPdf(filteredDepartments);
    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(filteredDepartments);
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
  };

  // Handle print all departments
  const handlePrintAll = async () => {
    if (!filteredDepartments || filteredDepartments.length === 0) {
      toast.error(t('departments.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setAllDepartmentsForPdf(filteredDepartments);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const chunks = calculateDynamicChunks(filteredDepartments);
      setPdfChunks(chunks);
      setPdfType('all');

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(`departments-pdf-all-${chunkIndex}`);
        if (!element) {
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`departments-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      }

      pdf.save(`ACE_Departments_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('departments.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Departments PDF error:', error);
      toast.error(t('departments.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف الأقسام' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle print single department
  const handlePrintDepartment = async (department) => {
    try {
      setDepartmentForPrint(department);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`department-pdf-${department.id}`);
      if (!element) {
        toast.error(t('departments.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`department-pdf-${department.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
          }
        }
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgData = canvas.toDataURL('image/png', 1.0);
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');

      const identifier = department.name || department.id;
      pdf.save(`ACE_Department_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Department PDF error:', err);
      toast.error(t('departments.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setDepartmentForPrint(null);
      setPdfType(null);
    }
  };

  // Handle export single department to Excel
  const handleExportDepartmentToExcel = (department) => {
    const data = [{
      [t('departments.table.name', { defaultValue: 'اسم القسم' })]: department.name || '—',
      [t('departments.table.description', { defaultValue: 'الوصف' })]: department.description || '—',
      [t('departments.table.createdAt', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(department.created_at) || '—'
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'قسم' : 'Department');
    const identifier = department.name || department.id;
    const fileName = `${isRTL ? 'قسم' : 'Department'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('departments.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all departments to Excel
  const handleExportAllToExcel = () => {
    if (!filteredDepartments || filteredDepartments.length === 0) {
      toast.error(t('departments.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const data = filteredDepartments.map((department, index) => ({
      [t('common.serialNumber', { defaultValue: '#' })]: index + 1,
      [t('departments.table.name', { defaultValue: 'اسم القسم' })]: department.name || '—',
      [t('departments.table.description', { defaultValue: 'الوصف' })]: department.description || '—',
      [t('departments.table.createdAt', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(department.created_at) || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'الأقسام' : 'Departments');
    const fileName = `${isRTL ? 'الأقسام' : 'Departments'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('departments.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('departments.notSet', { defaultValue: 'غير محدد' });
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Unified PDF page template renderer (for both Preview and Print)
  const renderPdfPage = (chunk, chunkIdx, totalPages, elementId = null) => {
    const hasActiveFilters = searchTerm.trim();
    const previewFormatDate = (ds) => ds ? new Date(ds).toLocaleDateString('en-US') : '--';

    return (
      <div
        id={elementId}
        style={{
          width: '794px',
          height: '1123px',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          padding: '40px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
          <div style={{
            border: '2px solid #000',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '60%',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              opacity: 0.05,
              zIndex: 0,
              pointerEvents: 'none'
            }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  display: 'block',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />

              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>
                  {isRTL ? 'تقرير الأقسام' : 'Departments Report'}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                  {isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalPages}
                </div>
                {hasActiveFilters && (
                  <div style={{ 
                    fontSize: '9px', 
                    color: '#444', 
                    marginTop: '6px', 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    backgroundColor: 'rgba(238, 242, 247, 0.5)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    <span>{t('common.search', { defaultValue: 'بحث' })}: {searchTerm.trim().replace(/\s+/g, ' ')}</span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '8%' }} /><col style={{ width: '30%' }} /><col style={{ width: '42%' }} /><col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={colStyles.serial}>#</th>
                      <th style={colStyles.name}>{t('departments.table.name')}</th>
                      <th style={colStyles.description}>{t('departments.table.description')}</th>
                      <th style={colStyles.created}>{t('departments.table.createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk && chunk.length > 0 ? (
                      chunk.map((department, idx) => {
                        const foundIdx = allDepartmentsForPdf.findIndex(d => String(d?.id) === String(department?.id));
                        const serial = foundIdx >= 0 ? foundIdx + 1 : chunkIdx * 25 + idx + 1;
                        const cellBgColor = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(245, 245, 245, 0.5)';
                        return (
                          <tr key={department.id} style={{ backgroundColor: cellBgColor }}>
                            <td style={{ ...colStyles.serial, backgroundColor: cellBgColor }}>{serial}</td>
                            <td style={{ ...colStyles.name, backgroundColor: cellBgColor }}>{department.name || '—'}</td>
                            <td style={{ ...colStyles.description, backgroundColor: cellBgColor }}>{department.description || '—'}</td>
                            <td style={{ ...colStyles.created, backgroundColor: cellBgColor }}>{previewFormatDate(department.created_at)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ border: '1px solid #000', padding: '15px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                          {t('departments.noData', { defaultValue: 'لا توجد بيانات' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                      borderTop: "4px double #000",
                      paddingTop: "8px",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Unified PDF single page template renderer (for both Preview and Print)
  const renderSinglePdfPage = (department, isPreview = false) => {
    const containerId = isPreview
      ? `preview-department-pdf-${department.id}`
      : `department-pdf-${department.id}`;

    return (
      <div
        id={containerId}
        style={{
          width: '794px',
          height: '1123px',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          padding: '40px',
          boxSizing: 'border-box',
          position: isPreview ? 'relative' : 'absolute',
          left: isPreview ? '0' : '-9999px',
          top: 0,
          boxShadow: isPreview ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none',
          overflow: 'hidden'
        }}
      >
        <div style={{
          border: '5px solid #000',
          padding: '3px',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          height: '1043px',
          overflow: 'hidden'
        }}>
          <div style={{
            border: '2px solid #000',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '95%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none'
            }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />

              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>
                  {isRTL ? 'تفاصيل القسم' : 'Department Details'}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{department.id}</div>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: '120px' }}>
                <div style={{
                  marginTop: '12px',
                  border: '2px solid #000',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                }}>
                  {[
                    { label: t('departments.table.name', { defaultValue: 'اسم القسم' }), value: department.name || '—' },
                    { label: t('departments.table.description', { defaultValue: 'الوصف' }), value: department.description || '—' },
                    { label: t('departments.table.createdAt', { defaultValue: 'تاريخ الإنشاء' }), value: formatDate(department.created_at) || '—' }
                  ].filter(row => row.value && row.value !== '—').map((row) => {
                    const pdfStyles = getPdfRowStyles(isRTL);
                    return (
                      <div key={row.label} style={pdfStyles.row}>
                        {isRTL ? (
                          <>
                            <div style={pdfStyles.value}>
                              {row.value}
                            </div>
                            <div style={pdfStyles.label}>
                              <span style={{ width: '100%', textAlign: isRTL ? 'right' : 'left', display: 'block' }}>
                                {row.label}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={pdfStyles.label}>
                              <span style={{ width: '100%', textAlign: isRTL ? 'right' : 'left', display: 'block' }}>
                                {row.label}
                              </span>
                            </div>
                            <div style={pdfStyles.value}>
                              {row.value}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{
                position: 'absolute',
                bottom: '90px',
                [isRTL ? 'left' : 'right']: '24px',
                fontSize: '10px',
                color: '#666',
                fontWeight: 'normal',
                direction: isRTL ? 'rtl' : 'ltr',
                zIndex: 10
              }}>
                {isRTL ? 'صفحة 1 من 1' : 'Page 1 of 1'}
              </div>

              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0
              }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if ((isLoading || isLoadingDepartments) && rawDepartments.length === 0) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {t('common.loading', { defaultValue: 'جاري التحميل...' })}
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      {/* ── Row 1: Title + Add ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, color: 'white' }}>
          {t('departments.title')}
        </h1>
        <button
          onClick={handleAdd}
          style={{
            backgroundColor: '#AC2000',
            color: 'white',
            border: 'none',
            padding: '10px 18px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          <FaPlus />
          {t('departments.add')}
        </button>
      </div>

      {/* ── Row 2: Search + Sort + PerPage ── */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <FaSearch style={{
            position: 'absolute',
            [isRTL ? 'right' : 'left']: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666',
            fontSize: '16px',
            zIndex: 1,
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            placeholder={t('departments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: isRTL ? '10px 42px 10px 14px' : '10px 14px 10px 42px',
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              direction: dir,
              textAlign,
              boxSizing: 'border-box'
            }}
          />
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150, color: 'white', fontSize: 13, direction: isRTL ? 'rtl' : 'ltr' }}>
          <span>{t('common.sortBy', { defaultValue: 'ترتيب حسب' })}</span>
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            style={{ padding: '10px 12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <option value="name">{t('departments.table.name', { defaultValue: 'اسم القسم' })}</option>
            <option value="created_at">{t('departments.table.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</option>
          </select>
        </label>
        <SortOrderSelect value={sortOrder} onChange={val => setSortOrder(val)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <label style={{ color: '#aaa', fontSize: '13px', whiteSpace: 'nowrap' }}>
            {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
          </label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => { const newValue = Math.max(1, perPageInput - 1); setPerPageInput(newValue); }}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '6px 0 0 6px', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >−</button>
            <input
              type="number"
              min="1"
              max={filteredDepartments?.length || 1000}
              value={perPageInput}
              onChange={(e) => { const value = Math.max(1, parseInt(e.target.value) || 1); setPerPageInput(value); }}
              style={{ width: '60px', padding: '6px 8px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderLeft: 'none', borderRight: 'none', borderRadius: 0, color: 'white', fontSize: '14px', textAlign: 'center' }}
            />
            <button
              onClick={() => { const maxValue = filteredDepartments?.length || 1000; const newValue = Math.min(maxValue, perPageInput + 1); setPerPageInput(newValue); }}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '0 6px 6px 0', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >+</button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
          >
            {t('common.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
      </div>

      {/* ── Row 3: Export / Print ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={handlePreviewAll}
          style={{ backgroundColor: '#444', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaEye /> {t('departments.preview.all', { defaultValue: 'معاينة PDF للكل' })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{ backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: isGeneratingPdf ? 0.6 : 1 }}
        >
          <FaPrint />
          {isGeneratingPdf ? t('departments.print.generating', { defaultValue: 'جاري الطباعة...' }) : t('departments.print.all', { defaultValue: 'طباعة PDF للكل' })}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaFileExcel />
          {t('departments.export.all', { defaultValue: 'تصدير Excel للكل' })}
        </button>
      </div>

      {/* جدول الأقسام */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #333'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '800px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  {t('departments.table.number')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  <FaBuilding className="mx-2" />
                  {t('departments.table.name')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  <FaFileAlt className="mx-2" />
                  {t('departments.table.description')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000',
                  whiteSpace: 'nowrap',
                  minWidth: '150px'
                }}>
                  <FaCalendarAlt className="mx-2" />
                  {t('departments.table.createdAt')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  {t('departments.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedDepartments && paginatedDepartments.length > 0 ? (
                paginatedDepartments.map((department, index) => (
                  <tr key={department.id} style={{
                    borderBottom: '1px solid #333'
                  }}>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      {sortOrder === 'asc'
                        ? (currentPageSafe - 1) * perPage + index + 1
                        : filteredDepartments.length - ((currentPageSafe - 1) * perPage + index)}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      {department.name}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      opacity: 0.9,
                      maxWidth: '300px',
                      wordWrap: 'break-word',
                      textAlign: 'center'
                    }}>
                      {department.description}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      opacity: 0.9,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      minWidth: '150px'
                    }}>
                      {formatDate(department.created_at)}
                    </td>
                    <td style={{
                      padding: '15px',
                      textAlign: 'center'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        <button
                          onClick={() => handleView(department)}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('departments.preview.single', { defaultValue: 'معاينة PDF' })}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handlePrintDepartment(department)}
                          disabled={isGeneratingPdf}
                          style={{
                            backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('departments.print.single', { defaultValue: 'طباعة' })}
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={() => handleExportDepartmentToExcel(department)}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('departments.export.single', { defaultValue: 'تصدير Excel' })}
                        >
                          <FaFileExcel />
                        </button>
                        <button
                          onClick={() => handleEdit(department.id)}
                          style={{
                            backgroundColor: '#B3B3B3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('btns.edit', { defaultValue: 'تعديل' })}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(department)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('btns.delete', { defaultValue: 'حذف' })}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                    {t('departments.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* الترقيم */}
        {totalPages > 1 && (() => {
          const pages = [];
          for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)) {
              pages.push(i);
            } else if (i === currentPageSafe - 3 || i === currentPageSafe + 3) {
              pages.push('...');
            }
          }
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                disabled={currentPageSafe === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('departments.pagination.previous')}
              </button>
              {pages.map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: p === currentPageSafe ? '#AC2000' : '#1a1f2e',
                      color: 'white',
                      border: '1px solid #333',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))}
                disabled={currentPageSafe === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('departments.pagination.next')}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="modal-header" style={{ border: 'none', borderBottom: '1px solid #333' }}>
                <h5 className="modal-title text-white" style={{ textAlign }}>{t('departments.delete.title')}</h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>{t('departments.delete.message')} "{departmentToDelete?.name}"؟</p>
                <p style={{ color: 'white' }}>{t('departments.delete.irreversible')}</p>
              </div>
              <div className="modal-footer" style={{ border: 'none' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: '#6c757d',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('departments.delete.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('departments.delete.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Off-screen Print Templates */}
      {departmentForPrint && pdfType === 'single' && renderSinglePdfPage(departmentForPrint, false)}

      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        <>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              style={{
                position: 'absolute',
                left: '-9999px',
                top: 0
              }}
            >
              {renderPdfPage(chunk, chunkIndex, pdfChunks.length, `departments-pdf-all-${chunkIndex}`)}
            </div>
          ))}
        </>
      )}

      {/* Preview Modal for Single Department */}
      <PdfPreviewModal
        isOpen={isPreviewSingleOpen && departmentForPreview}
        title={`${t('departments.title', { defaultValue: 'الأقسام' })} - ${departmentForPreview?.name || departmentForPreview?.id || ''}`}
        onClose={() => {
          setIsPreviewSingleOpen(false);
          setDepartmentForPreview(null);
        }}
        onPrint={() => {
          if (departmentForPreview) {
            handlePrintDepartment(departmentForPreview);
          }
        }}
        isGeneratingPdf={isGeneratingPdf}
      >
        {departmentForPreview && renderSinglePdfPage(departmentForPreview, true)}
      </PdfPreviewModal>

      {/* Preview Modal for All Departments */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t('departments.title', { defaultValue: 'الأقسام' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIdx) => (
            <div key={chunkIdx} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)' }}>
              {renderPdfPage(chunk, chunkIdx, pdfChunks.length)}
            </div>
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden Measurement Table */}
      <div
        id="departments-measurement-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '714px', // Content area width inside PDF layout
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '8%' }} /><col style={{ width: '30%' }} /><col style={{ width: '42%' }} /><col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
              <th style={colStyles.serial}>#</th>
              <th style={colStyles.name}>{t('departments.table.name')}</th>
              <th style={colStyles.description}>{t('departments.table.description')}</th>
              <th style={colStyles.created}>{t('departments.table.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {allDepartmentsForPdf.map((dept, idx) => (
              <tr key={dept.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td style={{ ...colStyles.name, direction: isRTL ? 'rtl' : 'ltr' }}>{dept.name || '—'}</td>
                <td style={{ ...colStyles.description, direction: isRTL ? 'rtl' : 'ltr' }}>{dept.description || '—'}</td>
                <td style={colStyles.created}>{dept.created_at ? new Date(dept.created_at).toLocaleDateString('en-US') : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Departments;
